import type { DataTableColumn } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { PaperclipIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo } from "react";
import { useParams } from "react-router";
import { apiClient } from "@/lib/api/client";
import { formatDay } from "@/lib/utils/date";
import { Classeur, type FilterDef } from "../components/classeur";
import {
  useClasseurFilters,
  useClasseurState,
} from "../components/classeur/use-classeur-data";

type FichierRef = { fichierId: number; filename: string };

type FicheQuestionItem = {
  id: string;
  numero: string | null;
  objet: string | null;
  dateEmission: string | null;
  resume: string | null;
  lot: { id: number; label: string } | null;
  fiche: FichierRef | null;
  pj: FichierRef[];
};

type FileLinkProps = {
  fichier: FichierRef;
  onOpen: (fichierId: number) => void;
};

function FileLink({ fichier, onOpen }: FileLinkProps) {
  return (
    <button
      type="button"
      onClick={() => onOpen(fichier.fichierId)}
      title={fichier.filename}
      className="max-w-40 cursor-pointer truncate text-blue-600 underline-offset-2 hover:underline dark:text-blue-400"
    >
      {fichier.filename}
    </button>
  );
}

/**
 * Écran 029 « Fiches Question / Réponse » — lecture seule, pour les
 * donneurs d'ordre (MOE, CT, AMO, SPS).
 *
 * L'API n'expose pas encore les auteurs et la réponse n'est pas modélisée :
 * seules les données renvoyées sont affichées, pas de colonnes fabriquées.
 */
export function FichesQREchanges() {
  const { yardId } = useParams<{ yardId: string }>();
  const [state, setState] = useClasseurState<FicheQuestionItem>();

  useEffect(() => {
    async function fetchData() {
      setState((prev) => ({ ...prev, isLoading: true }));
      const { data } = await apiClient.GET(
        "/api/yards/{yardId}/fiches-question",
        { params: { path: { yardId: yardId ?? "" } } },
      );
      if (isNotNil(data)) {
        const items = data.data.items.map((item) => ({
          ...item,
          id: String(item.id),
        }));
        setState({ items, total: data.data.total, isLoading: false });
      } else {
        setState({ items: [], total: 0, isLoading: false });
      }
    }
    fetchData();
  }, [yardId, setState]);

  const handleOpenFile = useCallback(
    async (fichierId: number) => {
      if (isNil(yardId)) return;
      const { data } = await apiClient.GET(
        "/api/yards/{yardId}/fichiers/{fichierId}/url",
        { params: { path: { yardId, fichierId: String(fichierId) } } },
      );
      if (isNotNil(data)) window.open(data.data.inlineUrl, "_blank");
    },
    [yardId],
  );

  const columns = useMemo<DataTableColumn<FicheQuestionItem>[]>(
    () => [
      {
        id: "numero",
        header: "N°",
        accessor: "numero",
        cell: ({ value }) => (value as string | null) ?? "-",
      },
      {
        id: "objet",
        header: "Objet",
        accessor: "objet",
        cellClassName: "max-w-56",
        cell: ({ value }) => {
          const objet = value as string | null;
          return (
            <span className="block truncate" title={objet ?? undefined}>
              {objet ?? "-"}
            </span>
          );
        },
      },
      {
        id: "dateEmission",
        header: "Date émission",
        accessor: "dateEmission",
        cell: ({ value }) =>
          isNotNil(value) ? formatDay(value as string) : "-",
      },
      {
        id: "resume",
        header: "Question posée",
        accessor: "resume",
        cellClassName: "max-w-52",
        cell: ({ value }) => {
          const resume = value as string | null;
          return (
            <span className="block truncate" title={resume ?? undefined}>
              {resume ?? "-"}
            </span>
          );
        },
      },
      {
        id: "lot",
        header: "Lot",
        accessor: (row) => row.lot?.label ?? "-",
        cellClassName: "max-w-32",
        cell: ({ row }) => (
          <span className="block truncate" title={row.lot?.label}>
            {row.lot?.label ?? "-"}
          </span>
        ),
      },
      {
        id: "fiche",
        header: "Fiche",
        accessor: (row) => row.fiche?.filename ?? "",
        cell: ({ row }) =>
          isNotNil(row.fiche) ? (
            <FileLink fichier={row.fiche} onOpen={handleOpenFile} />
          ) : (
            "-"
          ),
      },
      {
        id: "pj",
        header: "PJ",
        accessor: (row) => row.pj.length,
        cell: ({ row }) =>
          row.pj.length > 0 ? (
            // Liens en ligne (pas de retour à la ligne) pour garder une
            // hauteur de ligne fixe, conformément aux règles d'écran.
            <span className="flex items-center gap-2">
              <PaperclipIcon size={14} className="shrink-0 text-zinc-500" />
              {row.pj.map((pj) => (
                <FileLink
                  key={pj.fichierId}
                  fichier={pj}
                  onOpen={handleOpenFile}
                />
              ))}
            </span>
          ) : (
            "-"
          ),
      },
    ],
    [handleOpenFile],
  );

  // Options are derived from the full (unfiltered) list so they stay stable
  // while the lot filter is active.
  const filters = useMemo<FilterDef[]>(() => {
    const lots = new Map<string, string>();
    for (const item of state.items) {
      if (isNotNil(item.lot)) {
        lots.set(String(item.lot.id), item.lot.label);
      }
    }
    return [
      {
        key: "lot",
        label: "Lot",
        options: [...lots].map(([value, label]) => ({ value, label })),
      },
    ];
  }, [state.items]);

  const { activeFilters, onFilterChange } = useClasseurFilters({ filters });

  const filteredItems = useMemo(() => {
    const lotFilter = activeFilters.lot ?? [];
    if (lotFilter.length === 0) return state.items;
    return state.items.filter(
      (item) => isNotNil(item.lot) && lotFilter.includes(String(item.lot.id)),
    );
  }, [state.items, activeFilters]);

  return (
    <Classeur
      title="Fiches Question / Réponse"
      items={filteredItems}
      columns={columns}
      filters={filters}
      activeFilters={activeFilters}
      onFilterChange={onFilterChange}
      isLoading={state.isLoading}
      emptyMessage="Aucune fiche question"
      total={filteredItems.length}
    />
  );
}
